import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTags from "@/components/StatusTags/StatusTags";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import DeleteBusinessTravelAction from "@/pages/employee/business_travel/DeleteBusinessTravelAction";
import ExportBusinessTravelAction from "@/pages/employee/business_travel/ExportBusinessTravelAction";
import {
  BusinessTravel,
  useGetBusinessTravel,
  useGetBusinessTravelYears,
} from "@/pages/employee/business_travel/useBusinessTravel";
import { DeleteOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space } from "antd";
import { useState } from "react";

const columns: Column<BusinessTravel>[] = [
  {
    key: "external_id",
    label: "Travel ID",
    minWidth: "182px",
    searchable: true,
    sortable: true,
  },
  {
    key: "date",
    label: "Date",
    minWidth: "168px",
    sortable: true,
    searchable: true,
  },
  {
    key: "departure",
    label: "Departure",
    minWidth: "168px",
    sortable: true,
    searchable: true,
  },
  {
    key: "arrival",
    label: "Arrival",
    minWidth: "170px",
    searchable: true,
    sortable: true,
  },
  {
    key: "travel_mode",
    label: "Travel mode",
    minWidth: "170px",
    searchable: true,
    sortable: true,
    renderer: (record) => (
      <CrossReference to={`/employees/travel_modes/${record.travel_mode_id}`}>
        {record.travel_mode}
      </CrossReference>
    ),
  },
  {
    key: "distance",
    label: "Total km",
    minWidth: "140px",
    sortable: true,
  },
  {
    key: "owned",
    label: "Owned or leased",
    minWidth: "220px",
    maxWidth: "220px",
    sortable: true,
    filterable: true,
    renderer: (record) => (record.owned ? "Yes" : "No"),
  },
  {
    key: "impact",
    label: "Impact",
    minWidth: "300px",
    renderer: (record) => (
      <ImpactPill
        impact={record.impact}
        type="borderless"
        incomplete={!record.dataset_id}
        incompleteTooltip={"Travel mode needs an impact reference."}
      />
    ),
  },
];

function BusinessTravelTable() {
  const { year } = useDashboardYear();
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setMoreDropdownOpen(open);
  };

  const { data: yearsData, isLoading: yearsIsLoading } = useGetBusinessTravelYears();

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const {
    data,
    isLoading: dataIsLoading,
    isFetching: dataIsFetching,
  } = useGetBusinessTravel({
    year,
    filter,
    sort,
    search,
    pagination,
    enabled: !!year && !yearsIsLoading,
  });

  const isLoading = ((dataIsLoading || dataIsFetching) && !!year) || yearsIsLoading;

  const resetTable = () => {
    setPage(1);
  };

  return (
    <Row>
      <Col span={24}>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={yearsData}
                onChange={resetTable}
                isLoading={yearsIsLoading}
              />
              {isLoading ? (
                <Skeleton style={{ width: 180, height: 32 }} />
              ) : (
                <Space.Compact>
                  <ExportBusinessTravelAction year={year}>
                    <Button icon={<ExportOutlined />}>Export</Button>
                  </ExportBusinessTravelAction>
                  <MoreButton
                    onOpenChange={handleOpenChange}
                    open={moreDropdownOpen}
                    actions={
                      <DeleteBusinessTravelAction
                        year={year}
                        onSuccess={() => setMoreDropdownOpen(false)}
                      >
                        <ActionsPopOverItem
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete
                        </ActionsPopOverItem>
                      </DeleteBusinessTravelAction>
                    }
                  />
                </Space.Compact>
              )}
            </>
          }
          right={
            <>
              <StatusTags
                data={data}
                loading={isLoading}
              />
            </>
          }
        />
      </Col>
      <Col span={24}>
        <Table
          loading={isLoading}
          rowKey="id"
          data={data?.data}
          availableFilters={{
            owned: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          }}
          onSearchFilterSortPaginate={onSearchFilterSortPaginate}
          totalRows={data?.total_count}
          pagination={pagination}
          sort={sort}
          search={search}
          filter={filter}
          columns={columns}
        />
      </Col>
    </Row>
  );
}

export default BusinessTravelTable;
